"use client";

import {
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import NewCustomAsyncSelectDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";
import { Formik } from "formik";
import * as Yup from "yup";

import {
  getEvents,
  createEvent,
  updateEvent,
} from "../../../../services/EventService.js";
import { getBatchesByTrainers, fetchAllBatchOptions } from "../../../../services/BatchService.js";
import { getTrainerOptions, fetchAllTrainerOptions } from "../../../../services/TrainerService.js";
import { getClassrooms } from "../../../../services/ClassroomService.js";
import { toast } from '../../common/Toast/toast';
import {
  INVALID_SCHEDULE_MESSAGE,
  isPastSchedule,
  PAST_SCHEDULE_MESSAGE,
  toEventDateTime,
  todayInputValue,
} from '../../../../utils/eventSchedule';
import { getMe } from "../../../../services/AuthService.js";
import moment from "moment";
import {
  PRIORITY_NOT_SET,
  normalizePriority,
  priorityOptions,
} from "../../../../utils/eventPriority.js";

// ─── The three steps ──────────────────────────────────────────────
// One long scroll of eighteen fields asked everything at once. Split
// into what the event is, who it reaches, and where it happens -- the
// same three-step dialog the attendance and quiz forms use.
//
// A holiday has no venue, organiser or description, so it is a two-step
// form; the third tab is not shown at all rather than shown empty.
const EVENT_STEPS = [
  {
    label: "Basics",
    title: "What is the event?",
    hint: "Its kind, its name, how urgent it is and when it happens.",
  },
  {
    label: "Audience",
    title: "Who is it for?",
    hint: "The trainers running it, and the batches that should see it.",
  },
  {
    label: "Details",
    title: "Where does it happen?",
    hint: "The venue or link, who to ask about it, and what it covers.",
  },
];

const eventSteps = (isHoliday) =>
  isHoliday ? EVENT_STEPS.slice(0, 2) : EVENT_STEPS;

const evFilled = (v) => String(v ?? "").trim().length > 0;

// Whether a step has been answered well enough to move on. Values are
// checked as well as Formik's `errors`, because `errors` stays empty
// until validation runs and an untouched step would otherwise pass.
const eventStepComplete = (step, ev = {}, errs = {}, isHoliday = false) => {
  if (step === 1) {
    if (!evFilled(ev.category) || errs.category) return false;
    if (!evFilled(ev.priority) || errs.priority) return false;
    if (isHoliday) {
      return (
        evFilled(ev.startDateTime) &&
        !errs.startDateTime &&
        evFilled(ev.endDateTime) &&
        !errs.endDateTime
      );
    }
    return (
      evFilled(ev.name) &&
      !errs.name &&
      evFilled(ev.date) &&
      !errs.date &&
      evFilled(ev.time) &&
      !errs.time
    );
  }

  if (step === 2) {
    return (
      Array.isArray(ev.trainerIds) &&
      ev.trainerIds.length > 0 &&
      !errs.trainerIds &&
      Array.isArray(ev.batchIds) &&
      ev.batchIds.length > 0 &&
      !errs.batchIds
    );
  }

  if (step === 3) {
    if (isHoliday) return true;
    return (
      evFilled(ev.venueId) &&
      !errs.venueId &&
      evFilled(ev.organizerName) &&
      !errs.organizerName &&
      evFilled(ev.organizerEmail) &&
      !errs.organizerEmail &&
      evFilled(ev.organizerPhone) &&
      !errs.organizerPhone &&
      evFilled(ev.description) &&
      !errs.description &&
      !errs.onlineLink
    );
  }

  return true;
};

// The holiday pair is stored as ISO; <input type="datetime-local"> wants
// wall-clock text, so the two are converted at the edge.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

// ─── API response helpers ─────────────────────────────────────────────────────
const extractBatchList = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp?.data?.configuedBatches))
    return resp.data.configuedBatches;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.configuedBatches)) return resp.configuedBatches;
  return [];
};

const extractPagination = (resp) => {
  if (!resp) return null;
  return resp?.data?.pagination || resp?.pagination || null;
};

// ─────────────────────────────────────────────────────────────────────────────
/* A form value as trimmed text. A value that arrives as something other
   than a string -- a classroom number stored as a number, say -- made
   `.trim()` throw before the request was ever sent, and the reader saw
   "Submission failed" with nothing in the Network tab to explain it. */
const asText = (value) => (value == null ? "" : String(value)).trim();

/* What went wrong, in words the reader can act on: the server's own
   message when it sent one, otherwise why the request never got an
   answer. It used to fall back to "Submission failed" for all of these,
   which is why a failure with a clean Network tab could not be traced. */
const submitErrorMessage = (error) => {
  const server = error?.response?.data?.message?.message || error?.response?.data?.message;
  if (server) return `Error: ${server}`;
  if (error?.response) return `Error: ${error.response.status} ${error.response.statusText || ""}`.trim();
  if (error?.code === "ECONNABORTED") {
    return "The server took too long to answer. Check the list before trying again -- it may already be saved.";
  }
  if (error?.request) return "Could not reach the server. Check your connection and try again.";
  return `Could not save: ${error?.message || "unknown error"}`;
};

function AddEventModal({
  selectedDate,
  selectedEventId,
  userRole,
  onEventSaved,
  onCancel,
}) {
  const [eventData, setEventData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTrainerId, setCurrentTrainerId] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Which step is being asked for, which is on screen, and the fade
  // between them -- the shown step trails the real one by 160ms so the
  // old panel leaves before the new one arrives.
  const [step, setStep] = useState(1);
  const [shownStep, setShownStep] = useState(1);
  const [phase, setPhase] = useState("in");

  useEffect(() => {
    if (shownStep === step) return undefined;
    setPhase("out");
    const t = setTimeout(() => {
      setShownStep(step);
      setPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [step, shownStep]);

  // ── Trainer dropdown state ────────────────────────────────────────────────
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [trainerLoading, setTrainerLoading] = useState(false);
  const [trainerSearchLoading, setTrainerSearchLoading] = useState(false);
  const allTrainersLoadedRef = useRef(false);

  // Refs to avoid stale closures in scroll handlers
  const trainerPageRef = useRef(1);
  const trainerHasMoreRef = useRef(true);
  const trainerSearchRef = useRef("");
  const trainerLoadingRef = useRef(false);
  const trainerSearchPageRef = useRef(1);
  const trainerSearchHasMoreRef = useRef(true);
  const trainerCurrentSearchRef = useRef("");

  // ── Batch dropdown state ──────────────────────────────────────────────────
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchSearchLoading, setBatchSearchLoading] = useState(false)

  // Refs for batch pagination
  const batchPageRef = useRef(1);
  const batchHasMoreRef = useRef(true);
  const batchSearchRef = useRef("");
  const batchLoadingRef = useRef(false);
  const batchSearchPageRef = useRef(1);
  const batchSearchHasMoreRef = useRef(true);
  const batchCurrentSearchRef = useRef("");

  // ── Select-All Batch state ────────────────────────────────────────────────
  const [selectAllBatchLoading, setSelectAllBatchLoading] = useState(false);
  const [selectAllBatchDone, setSelectAllBatchDone] = useState(false);
  const [selectAllBatchTotal, setSelectAllBatchTotal] = useState(0);
  const selectAllBatchLoadingRef = useRef(false);

  // ── Classroom dropdown state ──────────────────────────────────────────────
  const [classroomOptions, setClassroomOptions] = useState([]);
  const [classroomLoading, setClassroomLoading] = useState(false);
  const [classroomSearchLoading, setClassroomSearchLoading] = useState(false);

  // Refs for classroom pagination
  const classroomPageRef = useRef(1);
  const classroomHasMoreRef = useRef(true);
  const classroomSearchRef = useRef("");
  const classroomLoadingRef = useRef(false);
  const classroomSearchPageRef = useRef(1);
  const classroomSearchHasMoreRef = useRef(true);
  const classroomCurrentSearchRef = useRef("");

  const hasLoaded = useRef(false);
  const trainerSeq = useRef(0);
  const batchSeq = useRef(0);
  const classroomSeq = useRef(0);
  const trainerIdsRef = useRef([]);
  const batchLabelCacheRef = useRef(new Map());

  const DEFAULT_ISO = new Date().toISOString();

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getCurrentUserIdFromData = (userData) => {
    if (!userData) return null;
    return userData.id || userData._id || userData.userId;
  };

  const getCurrentUserId = () => getCurrentUserIdFromData(currentUser);

  const mergePaginatedOptions = (prev, next) => {
    const map = new Map();
    (prev || []).forEach((o) => map.set(String(o?.value), o));
    (next || []).forEach((o) => map.set(String(o?.value), o));
    return Array.from(map.values());
  };

  const resolveBatchValue = (ids) => {
    const list = Array.isArray(ids) ? ids : [];
    return list
      .map((id) => String(id).trim())
      .filter(Boolean)
      .map((id) => ({
        value: id,
        label: batchLabelCacheRef.current.get(id) || id,
      }));
  };

  const resetSelectAllBatchState = useCallback(() => {
    setSelectAllBatchLoading(false);
    selectAllBatchLoadingRef.current = false;
    setSelectAllBatchDone(false);
    setSelectAllBatchTotal(0);
  }, []);

  // ── TRAINER PAGINATION ─────────────────────────────────────────────────────
  const fetchTrainerPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (trainerLoadingRef.current) return [];
      const seq = (trainerSeq.current += 1);
      trainerLoadingRef.current = true;
      setTrainerLoading(true);
      try {
        // { id, trainerName, trainerEmail } per row -- all the picker shows.
        const resp = await getTrainerOptions(PAGE_SIZE, page, search);
        if (seq !== trainerSeq.current) return [];
        const list = (resp?.data || []).map((t) => ({
          label: t.trainerName || `Trainer ${(t.id || "").slice(0, 6)}`,
          value: t.id,
          email: t.trainerEmail || "",
        }));
        const hasNext = Boolean(resp?.pagination?.hasNext);
        setTrainerOptions((prev) => {
          if (append) return mergePaginatedOptions(prev, list);
          /* The chips are drawn by looking each chosen id up in these
             options, so replacing the list outright blanks a trainer the
             user has already picked but who is not on this page. */
          const chosen = (prev || []).filter((o) =>
            trainerIdsRef.current.some((id) => String(id) === String(o.value)),
          );
          return mergePaginatedOptions(chosen, list);
        });
        trainerPageRef.current = page;
        trainerSearchRef.current = search;
        trainerHasMoreRef.current = hasNext;
        return list;
      } catch (err) {
        if (err.response?.status !== 401)
          console.error("Error loading trainers:", err);
        setTrainerOptions((prev) => (append ? prev : []));
        trainerHasMoreRef.current = false;
        return [];
      } finally {
        trainerLoadingRef.current = false;
        setTrainerLoading(false);
      }
    },
    [],
  );

  const fetchTrainerSearchPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (trainerLoadingRef.current) return [];
      const seq = (trainerSeq.current += 1);
      trainerLoadingRef.current = true;
      setTrainerSearchLoading(true);
      try {
        // { id, trainerName, trainerEmail } per row -- all the picker shows.
        const resp = await getTrainerOptions(PAGE_SIZE, page, search);
        if (seq !== trainerSeq.current) return [];
        const list = (resp?.data || []).map((t) => ({
          label: t.trainerName || `Trainer ${(t.id || "").slice(0, 6)}`,
          value: t.id,
          email: t.trainerEmail || "",
        }));
        const hasNext = Boolean(resp?.pagination?.hasNext);
        
        setTrainerOptions((prev) => {
          if (append) return mergePaginatedOptions(prev, list);
          /* The chips are drawn by looking each chosen id up in these
             options, so replacing the list outright blanks a trainer the
             user has already picked but who is not on this page. */
          const chosen = (prev || []).filter((o) =>
            trainerIdsRef.current.some((id) => String(id) === String(o.value)),
          );
          return mergePaginatedOptions(chosen, list);
        });
        
        trainerSearchPageRef.current = page;
        trainerSearchHasMoreRef.current = hasNext;
        return list;
      } catch (err) {
        console.error("Error loading search trainers:", err);
        if (!append) setTrainerOptions([]);
        return [];
      } finally {
        trainerLoadingRef.current = false;
        setTrainerSearchLoading(false);
      }
    },
    [],
  );

  const loadTrainerOptions = useCallback(
    async (inputValue, page = 1) => {
      if (allTrainersLoadedRef.current && !inputValue) return trainerOptions;
      
      if (inputValue) {
        trainerCurrentSearchRef.current = inputValue;
        trainerSearchPageRef.current = page;
        return await fetchTrainerSearchPage({
          page: page,
          search: String(inputValue || ""),
          append: page > 1,
        });
      } else {
        trainerPageRef.current = page;
        return await fetchTrainerPage({
          page: page,
          search: "",
          append: page > 1,
        });
      }
    },
    [fetchTrainerPage, fetchTrainerSearchPage, trainerOptions],
  );

  const handleTrainerMenuOpen = useCallback(async () => {
    if (allTrainersLoadedRef.current) return;
    if (trainerOptions.length > 0) return;
    if (trainerLoadingRef.current) return;
    await fetchTrainerPage({ page: 1, search: "", append: false });
  }, [trainerOptions.length, fetchTrainerPage]);

  const handleTrainerScrollToBottom = useCallback(async () => {
    if (allTrainersLoadedRef.current) return;
    if (trainerLoadingRef.current) return;
    const term = trainerCurrentSearchRef.current;
    if (term) {
      if (!trainerSearchHasMoreRef.current) return;
      await fetchTrainerSearchPage({
        page: trainerSearchPageRef.current + 1,
        search: term,
        append: true,
      });
      return;
    }
    if (!trainerHasMoreRef.current) return;
    await fetchTrainerPage({
      page: trainerPageRef.current + 1,
      search: trainerSearchRef.current,
      append: true,
    });
  }, [fetchTrainerPage, fetchTrainerSearchPage]);

  /* Typing asks the SERVER for matches. Without this the dropdown only
     ever filtered the page or two it had already fetched --
     NewCustomAsyncSelectDropdown filters `options` locally unless the
     page hands it onInputChange -- so a trainer further down the
     directory could not be found by name and the list looked as though
     it had not loaded. The component debounces 500ms before calling
     this. The search pagers and fetchTrainerSearchPage were already
     built for exactly this and had simply never been wired up. */
  const handleTrainerInputChange = useCallback(
    async (value) => {
      const term = String(value || "").trim();
      if (term === trainerCurrentSearchRef.current) return;
      trainerCurrentSearchRef.current = term;
      // A search is a different question, so Select-all's answer no
      // longer covers it.
      allTrainersLoadedRef.current = false;
      trainerSearchPageRef.current = 1;
      trainerSearchHasMoreRef.current = true;
      if (!term) {
        trainerPageRef.current = 1;
        trainerHasMoreRef.current = true;
        await fetchTrainerPage({ page: 1, search: "", append: false });
        return;
      }
      await fetchTrainerSearchPage({ page: 1, search: term, append: false });
    },
    [fetchTrainerPage, fetchTrainerSearchPage],
  );

  const handleTrainerSearchScrollToBottom = useCallback(async (searchTerm) => {
    if (trainerLoadingRef.current || !trainerSearchHasMoreRef.current) return;
    await fetchTrainerSearchPage({
      page: trainerSearchPageRef.current + 1,
      search: searchTerm,
      append: true,
    });
  }, [fetchTrainerSearchPage]);

  const refreshTrainers = useCallback(async () => {
    allTrainersLoadedRef.current = false;
    trainerSeq.current += 1;
    trainerSearchRef.current = "";
    trainerPageRef.current = 1;
    trainerHasMoreRef.current = true;
    trainerSearchPageRef.current = 1;
    trainerSearchHasMoreRef.current = true;
    trainerCurrentSearchRef.current = "";
    await fetchTrainerPage({ page: 1, search: "", append: false });
  }, [fetchTrainerPage]);

  // ── BATCH PAGINATION ───────────────────────────────────────────────────────
  const fetchBatchPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (batchLoadingRef.current) return [];
      const seq = (batchSeq.current += 1);
      batchLoadingRef.current = true;
      setBatchLoading(true);
      try {
        const trainerIds = Array.isArray(trainerIdsRef.current)
          ? trainerIdsRef.current.map((x) => String(x).trim()).filter(Boolean)
          : [];

        if (!trainerIds.length) {
          if (seq !== batchSeq.current) return [];
          setFilteredBatches([]);
          batchPageRef.current = 1;
          batchSearchRef.current = search;
          batchHasMoreRef.current = false;
          return [];
        }

        const resp = await getBatchesByTrainers(
          trainerIds,
          PAGE_SIZE,
          page,
          search,
          "name",
        );
        if (seq !== batchSeq.current) return [];

        const rawList = extractBatchList(resp);
        const pagination = extractPagination(resp);

        const list = rawList.map((b) => ({
          value: String(b.id),
          label: b.name,
          trainerId: b.trainerId || null,
        }));
        list.forEach((o) =>
          batchLabelCacheRef.current.set(
            String(o.value),
            String(o.label || o.value),
          ),
        );

        const hasNext =
          Boolean(pagination?.hasNext) || Boolean(pagination?.hasNextPage);

        setFilteredBatches((prev) =>
          append ? mergePaginatedOptions(prev, list) : list,
        );
        batchPageRef.current = page;
        
        batchSearchRef.current = search;
        batchHasMoreRef.current = hasNext;

        return list;
      } catch (err) {
        console.error("Error loading batches:", err);
        setFilteredBatches((prev) => (append ? prev : []));
        batchHasMoreRef.current = false;
        return [];
      } finally {
        batchLoadingRef.current = false;
        setBatchLoading(false);
      }
    },
    [],
  );

  const fetchBatchSearchPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (batchLoadingRef.current) return [];
      const seq = (batchSeq.current += 1);
      batchLoadingRef.current = true;
      setBatchSearchLoading(true);
      try {
        const trainerIds = Array.isArray(trainerIdsRef.current)
          ? trainerIdsRef.current.map((x) => String(x).trim()).filter(Boolean)
          : [];

        if (!trainerIds.length) {
          if (seq !== batchSeq.current) return [];
          setFilteredBatches([]);
          return [];
        }

        const resp = await getBatchesByTrainers(
          trainerIds,
          PAGE_SIZE,
          page,
          search,
          "name",
        );
        if (seq !== batchSeq.current) return [];

        const rawList = extractBatchList(resp);
        const pagination = extractPagination(resp);

        const list = rawList.map((b) => ({
          value: String(b.id),
          label: b.name,
          trainerId: b.trainerId || null,
        }));
        list.forEach((o) =>
          batchLabelCacheRef.current.set(
            String(o.value),
            String(o.label || o.value),
          ),
        );

        const hasNext =
          Boolean(pagination?.hasNext) || Boolean(pagination?.hasNextPage);

        if (append) {
          setFilteredBatches((prev) => mergePaginatedOptions(prev, list));
        } else {
          setFilteredBatches(list);
        }
        
        batchSearchPageRef.current = page;
        batchSearchHasMoreRef.current = hasNext;

        return list;
      } catch (err) {
        console.error("Error loading search batches:", err);
        if (!append) setFilteredBatches([]);
        return [];
      } finally {
        batchLoadingRef.current = false;
        setBatchSearchLoading(false);
      }
    },
    [],
  );

  const loadBatchOptions = useCallback(
    async (inputValue, page = 1) => {
      if (selectAllBatchLoadingRef.current) return filteredBatches;
      
      if (inputValue) {
        batchCurrentSearchRef.current = inputValue;
        batchSearchPageRef.current = page;
        return await fetchBatchSearchPage({
          page: page,
          search: String(inputValue || ""),
          append: page > 1,
        });
      } else {
        batchPageRef.current = page;
        return await fetchBatchPage({
          page: page,
          search: "",
          append: page > 1,
        });
      }
    },
    [fetchBatchPage, fetchBatchSearchPage, filteredBatches],
  );

  const handleBatchMenuOpen = useCallback(async () => {
    if (batchLoadingRef.current) return;
    if (selectAllBatchLoadingRef.current) return;
    await fetchBatchPage({ page: 1, search: "", append: false });
  }, [fetchBatchPage]);

  const handleBatchScrollToBottom = useCallback(async () => {
    if (selectAllBatchLoadingRef.current) return;
    if (batchLoadingRef.current || !batchHasMoreRef.current) return;
    await fetchBatchPage({
      page: batchPageRef.current + 1,
      search: batchSearchRef.current,
      append: true,
    });
  }, [fetchBatchPage]);

  const handleBatchSearchScrollToBottom = useCallback(async (searchTerm) => {
    if (batchLoadingRef.current || !batchSearchHasMoreRef.current) return;
    await fetchBatchSearchPage({
      page: batchSearchPageRef.current + 1,
      search: searchTerm,
      append: true,
    });
  }, [fetchBatchSearchPage]);

  const refreshBatches = useCallback(async () => {
    batchSeq.current += 1;
    batchSearchRef.current = "";
    batchPageRef.current = 1;
    batchHasMoreRef.current = true;
    batchSearchPageRef.current = 1;
    batchSearchHasMoreRef.current = true;
    batchCurrentSearchRef.current = "";
    resetSelectAllBatchState();
    await fetchBatchPage({ page: 1, search: "", append: false });
  }, [fetchBatchPage, resetSelectAllBatchState]);

  // ── CLASSROOM PAGINATION ───────────────────────────────────────────────────
  const fetchClassroomPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (classroomLoadingRef.current) return [];
      const seq = (classroomSeq.current += 1);
      classroomLoadingRef.current = true;
      setClassroomLoading(true);
      try {
        const resp = await getClassrooms(PAGE_SIZE, page, search, false);
        if (seq !== classroomSeq.current) return [];
        const list = (resp?.data || []).map((c) => ({
          value: c.id,
          label: c.classroomNo,
          classroomNo: c.classroomNo,
        }));
        const hasNext = Boolean(resp?.pagination?.hasNext);
        setClassroomOptions((prev) =>
          append ? mergePaginatedOptions(prev, list) : list,
        );
        classroomPageRef.current = page;
        classroomSearchRef.current = search;
        classroomHasMoreRef.current = hasNext;
        return list;
      } catch (err) {
        console.error("Error loading classrooms:", err);
        setClassroomOptions((prev) => (append ? prev : []));
        classroomHasMoreRef.current = false;
        return [];
      } finally {
        classroomLoadingRef.current = false;
        setClassroomLoading(false);
      }
    },
    [],
  );

  const fetchClassroomSearchPage = useCallback(
    async ({ page = 1, search = "", append = false } = {}) => {
      if (classroomLoadingRef.current) return [];
      const seq = (classroomSeq.current += 1);
      classroomLoadingRef.current = true;
      setClassroomSearchLoading(true);
      try {
        const resp = await getClassrooms(PAGE_SIZE, page, search, false);
        if (seq !== classroomSeq.current) return [];
        const list = (resp?.data || []).map((c) => ({
          value: c.id,
          label: c.classroomNo,
          classroomNo: c.classroomNo,
        }));
        const hasNext = Boolean(resp?.pagination?.hasNext);
        
        if (append) {
          setClassroomOptions((prev) => mergePaginatedOptions(prev, list));
        } else {
          setClassroomOptions(list);
        }
        
        classroomSearchPageRef.current = page;
        classroomSearchHasMoreRef.current = hasNext;
        return list;
      } catch (err) {
        console.error("Error loading search classrooms:", err);
        if (!append) setClassroomOptions([]);
        return [];
      } finally {
        classroomLoadingRef.current = false;
        setClassroomSearchLoading(false);
      }
    },
    [],
  );

  const loadClassroomOptions = useCallback(
    async (inputValue, page = 1) => {
      if (inputValue) {
        classroomCurrentSearchRef.current = inputValue;
        classroomSearchPageRef.current = page;
        return await fetchClassroomSearchPage({
          page: page,
          search: String(inputValue || ""),
          append: page > 1,
        });
      } else {
        classroomPageRef.current = page;
        return await fetchClassroomPage({
          page: page,
          search: "",
          append: page > 1,
        });
      }
    },
    [fetchClassroomPage, fetchClassroomSearchPage],
  );

  const handleClassroomMenuOpen = useCallback(async () => {
    if (classroomOptions.length > 0) return;
    if (classroomLoadingRef.current) return;
    await fetchClassroomPage({ page: 1, search: "", append: false });
  }, [classroomOptions.length, fetchClassroomPage]);

  const handleClassroomScrollToBottom = useCallback(async () => {
    if (classroomLoadingRef.current || !classroomHasMoreRef.current) return;
    await fetchClassroomPage({
      page: classroomPageRef.current + 1,
      search: classroomSearchRef.current,
      append: true,
    });
  }, [fetchClassroomPage]);

  const handleClassroomSearchScrollToBottom = useCallback(async (searchTerm) => {
    if (classroomLoadingRef.current || !classroomSearchHasMoreRef.current) return;
    await fetchClassroomSearchPage({
      page: classroomSearchPageRef.current + 1,
      search: searchTerm,
      append: true,
    });
  }, [fetchClassroomSearchPage]);

  const refreshClassrooms = useCallback(async () => {
    classroomSeq.current += 1;
    classroomSearchRef.current = "";
    classroomPageRef.current = 1;
    classroomHasMoreRef.current = true;
    classroomSearchPageRef.current = 1;
    classroomSearchHasMoreRef.current = true;
    classroomCurrentSearchRef.current = "";
    await fetchClassroomPage({ page: 1, search: "", append: false });
  }, [fetchClassroomPage]);

  // ── INIT ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    try {
      setLoading(true);
      const userProfile = await getMe();
      const userData = userProfile.data;
      const userId = getCurrentUserIdFromData(userData);

      if (!userId) {
        toast.error("Unable to fetch user information");
        return;
      }

      setCurrentUser(userData);

      // Check every profile, not just profiles[0] -- a user can hold more
      // than one role (e.g. Trainer plus a separately-granted scoped role),
      // and relying on index 0 wrongly showed the full "pick any trainer"
      // selector to trainers whose Trainer profile wasn't first in the list.
      const profileLabels = (userData.profiles || []).map((p) => p?.label?.toLowerCase()).filter(Boolean);
      const isTrainerUser = profileLabels.includes("trainer");
      const isAdminUser = userData.isSuperAdmin || profileLabels.includes("admin") || profileLabels.includes("superadmin") || profileLabels.includes("crm");

      setIsTrainer(isTrainerUser);
      

      if (isTrainerUser) setCurrentTrainerId(userId);

      if (selectedEventId) {
        await fetchEventDetails(
          selectedEventId,
          isTrainerUser,
          isAdminUser,
          userId,
          userData,
        );
      } else {
        const defaultDate = selectedDate || "";
        const defaultTrainerName =
          isTrainerUser && userData
            ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim()
            : "";

        setEventData([
          {
            name: "",
            date: defaultDate,
            time: "09:00",
            venueId: "",
            onlineLink: "",
            offlineAddress: "",
            organizerName: "",
            organizerEmail: "",
            organizerPhone: "",
            description: "",
            agenda: "",
            highlights: "",
            category: "",
            priority: PRIORITY_NOT_SET,
            classroomNumber: "",
            trainerIds: isTrainerUser ? [userId] : [],
            trainerName: defaultTrainerName,
            batchIds: [],
            startDateTime: "",
            endDateTime: "",
          },
        ]);
        setIsEditing(false);
      }

      setHasInitialized(true);
    } catch (err) {
      console.error("Failed to initialize component:", err);
      toast.error("Failed to initialize component");
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetails = async (
    eventId,
    isTrainerUser,
    isAdminUser,
    userId,
    userData,
  ) => {
    try {
      const events = await getEvents(5000000);
      const selectedEvent = events.data.find(
        (event) => event.id.toString() === eventId,
      );

      if (!selectedEvent) {
        const defaultDate = selectedDate || "";
        const defaultTrainerName =
          isTrainerUser && userData
            ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim()
            : "";
        setEventData([
          {
            name: "",
            date: defaultDate,
            time: "09:00",
            venueId: "",
            onlineLink: "",
            offlineAddress: "",
            organizerName: "",
            organizerEmail: "",
            organizerPhone: "",
            description: "",
            agenda: "",
            highlights: "",
            category: "",
            priority: PRIORITY_NOT_SET,
            classroomNumber: "",
            trainerIds: isTrainerUser && userId ? [userId] : [],
            trainerName: defaultTrainerName,
            batchIds: [],
            startDateTime: "",
            endDateTime: "",
          },
        ]);
        setIsEditing(false);
        return;
      }

      let dateString = "";
      let timeString = "09:00";
      if (selectedEvent.date) {
        dateString = new Date(selectedEvent.date).toISOString().split("T")[0];
      }
      if (selectedEvent.time) {
        if (
          selectedEvent.time.includes("T") ||
          selectedEvent.time.includes("Z")
        ) {
          timeString = moment.utc(selectedEvent.time).format("HH:mm");
        } else if (selectedEvent.time.includes(":")) {
          timeString = selectedEvent.time.slice(0, 5);
        }
      }

      const trainerIdsArray = selectedEvent.trainerId
        ? Array.isArray(selectedEvent.trainerId)
          ? selectedEvent.trainerId
              .map((t) => t?.id)
              .filter(Boolean)
          : selectedEvent.trainerId.split(",").map((t) => t.trim())
        : [];

      const batchIdsArray = selectedEvent.batchId
        ? Array.isArray(selectedEvent.batchId)
          ? selectedEvent.batchId.map((b) => b?.id || b).filter(Boolean)
          : selectedEvent.batchId.split(",").map((b) => b.trim())
        : [];

      if (Array.isArray(selectedEvent.trainerId)) {
        const trainerOpts = selectedEvent.trainerId
          .filter((t) => t && t.id)
          .map((t) => ({
            value: t.id,
            label:
              t.trainerName ||
              `Trainer ${(t.id || "").slice(0, 6)}`,
            specialization: t.specialization || "",
            email: t.trainerEmail || "",
          }));
        if (trainerOpts.length) setTrainerOptions(trainerOpts);
      }

      if (Array.isArray(selectedEvent.batchId)) {
        const batchOpts = selectedEvent.batchId
          .filter((b) => b && (b.id || typeof b === "string"))
          .map((b) => {
            const bid = String(b?.id || b);
            const label = b?.name || bid;
            batchLabelCacheRef.current.set(bid, label);
            return { value: bid, label, trainerId: b.trainerId || null };
          });
        if (batchOpts.length) setFilteredBatches(batchOpts);
      }

      if (Array.isArray(selectedEvent.venueId)) {
        const venueOpts = selectedEvent.venueId
          .filter((v) => v && v.id)
          .map((v) => ({
            value: v.id,
            label: v.classroomNo || v.name || "Unknown",
            classroomNo: v.classroomNo || "",
          }));
        if (venueOpts.length) setClassroomOptions(venueOpts);
      }

      trainerIdsRef.current = trainerIdsArray.map((x) => String(x).trim());

      const eventDataObj = {
        ...selectedEvent,
        date: dateString,
        time: timeString,
        trainerIds: trainerIdsArray.map((x) => String(x).trim()),
        batchIds: batchIdsArray.map((x) => String(x).trim()),
        venueId: Array.isArray(selectedEvent.venueId)
          ? selectedEvent.venueId[0]?.id || ""
          : selectedEvent.venueId || "",
        startDateTime: selectedEvent.startDateTime || "",
        endDateTime: selectedEvent.endDateTime || "",
        // Events created before priorities existed come back without one.
        priority: normalizePriority(selectedEvent.priority),
      };

      if (selectedEvent.category === "Holiday") eventDataObj.name = "Holiday";

      setEventData([eventDataObj]);
      setIsEditing(true);
    } catch (error) {
      console.error("Error fetching event details:", error);
      toast.error("Error fetching event details!");
    }
  };

  // Sync trainerIdsRef on edit / trainer-user init
  useEffect(() => {
    if (!hasInitialized) return;
    if (isEditing && eventData?.[0]?.trainerIds?.length > 0) {
      trainerIdsRef.current = eventData[0].trainerIds.map((x) => String(x));
    } else if (isTrainer && !isEditing && currentTrainerId) {
      trainerIdsRef.current = [String(currentTrainerId)];
    }
  }, [hasInitialized, isEditing, isTrainer, currentTrainerId]);

  // ── Form config ────────────────────────────────────────────────────────────
  const categoryOptions = [
    { value: "Event", label: "Event" },
    { value: "Mock Interview", label: "Mock Interview" },
    { value: "Holiday", label: "Holiday" },
  ];

  const validationSchema = Yup.object().shape({
    events: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.notRequired(),
          otherwise: (s) => s.required("Event Name is required"),
        }),
        startDateTime: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.required("This is the required field"),
          otherwise: (s) => s.notRequired(),
        }),
        endDateTime: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.required("This is the required field"),
          otherwise: (s) => s.notRequired(),
        }),
        date: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.notRequired(),
          otherwise: (s) => s.required("Date is required"),
        }),
        time: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Start Time is required"),
        }),
        venueId: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Venue is required"),
        }),
        onlineLink: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.test("holiday-link", "", () => true),
          otherwise: (s) => s.url("Invalid URL").nullable().notRequired(),
        }),
        offlineAddress: Yup.string().nullable().notRequired(),
        organizerName: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Organizer Name is required"),
        }),
        organizerEmail: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.test("holiday-email", "", () => true),
          otherwise: (s) =>
            s.email("Invalid email").required("Organizer Email is required"),
        }),
        organizerPhone: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Organizer Phone is required"),
        }),
        description: Yup.string().when("category", {
          is: "Holiday",
          then: (s) => s.nullable().notRequired(),
          otherwise: (s) => s.required("Description is required"),
        }),
        agenda: Yup.string().nullable().notRequired(),
        highlights: Yup.string().nullable().notRequired(),
        category: Yup.string().required("Category is required"),
        priority: Yup.string()
          .oneOf(priorityOptions.map((o) => o.value))
          .required("Priority is required"),
        classroomNumber: Yup.string().nullable().notRequired(),
        trainerIds: Yup.array()
          .min(1, "At least one Trainer is required")
          .required("Trainer is required"),
        batchIds: Yup.array()
          .min(1, "At least one Batch is required")
          .required("Batch is required"),
      }),
    ),
  });

  const getInitialValues = () => {
    const userId = getCurrentUserId();
    if (isEditing && eventData?.length > 0) {
      return {
        events: [
          {
            ...eventData[0],
            trainerIds: eventData[0].trainerIds || [],
            batchIds: eventData[0].batchIds || [],
          },
        ],
      };
    }
    return {
      events: [
        {
          name: "",
          date: selectedDate || "",
          time: "09:00",
          venueId: "",
          onlineLink: "",
          offlineAddress: "",
          organizerName: "",
          organizerEmail: "",
          organizerPhone: "",
          description: "",
          agenda: "",
          highlights: "",
          category: "",
          priority: PRIORITY_NOT_SET,
          classroomNumber: "",
          trainerIds: isTrainer && userId ? [String(userId)] : [],
          trainerName:
            isTrainer && currentUser
              ? `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim()
              : "",
          batchIds: [],
          startDateTime: "",
          endDateTime: "",
        },
      ],
    };
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleCategoryChange = (selectedOption, setFieldValue) => {
    const category = selectedOption?.value || "";
    setFieldValue("events[0].category", category);
    if (category === "Holiday") {
      [
        "name",
        "date",
        "time",
        "venueId",
        "onlineLink",
        "offlineAddress",
        "organizerName",
        "organizerEmail",
        "organizerPhone",
        "description",
        "agenda",
        "highlights",
      ].forEach((f) => setFieldValue(`events[0].${f}`, ""));
      setFieldValue(
        "events[0].startDateTime",
        new Date(
          eventData?.[0]?.date || selectedDate || new Date(),
        ).toISOString(),
      );
      setFieldValue(
        "events[0].endDateTime",
        new Date(
          new Date(
            eventData?.[0]?.date || selectedDate || new Date(),
          ).getTime() + 86400000,
        ).toISOString(),
      );
    } else {
      setFieldValue("events[0].startDateTime", "");
      setFieldValue("events[0].endDateTime", "");
      setFieldValue("events[0].name", "");
      setFieldValue("events[0].venueId", "");
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (userRole === "student") {
      toast.warning("Students cannot submit events!");
      setSubmitting(false);
      return;
    }
    try {
      if (!values.events?.length) {
        toast.error("No events to submit!");
        setSubmitting(false);
        return;
      }
      const event = values.events[0];

      // A new event cannot be set for a time that has gone; an existing one
      // stays editable. POST /events refuses it too.
      if (!isEditing && event.category !== "Holiday" && isPastSchedule(event.date, event.time)) {
        toast.error(PAST_SCHEDULE_MESSAGE);
        setSubmitting(false);
        return;
      }

      const payload = {
        name: event.name,
        date: event.date,
        time: event.time,
        venueId: event.venueId || null,
        onlineLink: asText(event.onlineLink) || null,
        offlineAddress: asText(event.offlineAddress) || null,
        organizerName: event.organizerName || null,
        organizerEmail: event.organizerEmail || null,
        organizerPhone: event.organizerPhone || null,
        description: event.description || null,
        agenda: event.agenda || null,
        highlights: asText(event.highlights) || null,
        category: event.category,
        priority: normalizePriority(event.priority),
        classroomNumber: asText(event.classroomNumber) || null,
        startDateTime: event.startDateTime || null,
        endDateTime: event.endDateTime || null,
      };

      payload.trainerId =
        event.trainerIds?.length > 0 ? event.trainerIds.join(",") : "";
      payload.batchId =
        event.batchIds?.length > 0 ? event.batchIds.join(",") : "";

      if (payload.category === "Holiday") {
        payload.name = "Holiday";
        payload.time = DEFAULT_ISO;
        payload.date = DEFAULT_ISO;
        payload.startDateTime = event.startDateTime || new Date().toISOString();
        payload.endDateTime =
          event.endDateTime || new Date(Date.now() + 86400000).toISOString();
        payload.organizerName = "N/A";
        payload.organizerEmail = "N/A";
        payload.organizerPhone = "N/A";
        payload.description = "N/A";
        payload.agenda = "N/A";
        payload.highlights = "N/A";
        payload.venueId = "N/A";
        payload.onlineLink = "N/A";
        payload.offlineAddress = "N/A";
        payload.classroomNumber = "N/A";
      } else {
        const when = toEventDateTime(event.date, event.time);
        if (!when) {
          toast.error(INVALID_SCHEDULE_MESSAGE);
          return;
        }
        payload.date = when.date;
        payload.time = when.time;
        payload.startDateTime = DEFAULT_ISO;
        payload.endDateTime = DEFAULT_ISO;
      }

      if (isEditing) {
        await updateEvent(selectedEventId, payload);
      } else {
        await createEvent(payload);
      }

      toast.success(`Event ${isEditing ? "updated" : "created"} successfully!`);
      resetForm();
      setEventData(null);
      setIsEditing(false);
      setFilteredBatches([]);
      resetSelectAllBatchState();
      setTimeout(() => onEventSaved(), 500);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(submitErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Trainer/Batch change handlers ──────────────────────────────────────────
  const onTrainersChange = (
    selectedOptions,
    setFieldValue,
    index,
    currentBatchIds,
  ) => {
    if (isTrainer && !isEditing) {
      toast.warning("As a trainer, you cannot change the trainer selection");
      return;
    }

    const selectedTrainerIds = selectedOptions
      ? selectedOptions.map((o) => o.value)
      : [];

    setFieldValue(`events[${index}].trainerIds`, selectedTrainerIds);
    setFieldValue(
      `events[${index}].trainerName`,
      selectedOptions ? selectedOptions.map((o) => o.label).join(", ") : "",
    );

    allTrainersLoadedRef.current = false;
    trainerIdsRef.current = selectedTrainerIds.map((x) => String(x));

    setFilteredBatches([]);
    batchPageRef.current = 1;
    
    batchHasMoreRef.current = true;
    batchSearchRef.current = "";
    resetSelectAllBatchState();
    setFieldValue(`events[${index}].batchIds`, []);
  };

  const onBatchesChange = (selectedOptions, setFieldValue, index) => {
    (selectedOptions || []).forEach((o) =>
      batchLabelCacheRef.current.set(
        String(o.value),
        String(o.label || o.value),
      ),
    );
    setFieldValue(
      `events[${index}].batchIds`,
      selectedOptions ? selectedOptions.map((o) => o.value) : [],
    );
    if (selectAllBatchDone) {
      resetSelectAllBatchState();
    }
  };

  // ── Select-All Trainers ────────────────────────────────────────────────────
  const handleSelectAllTrainers = async (isChecked, setFieldValue, index) => {
    if (!isChecked) {
      setFieldValue(`events[${index}].trainerIds`, []);
      setFieldValue(`events[${index}].trainerName`, "");
      trainerIdsRef.current = [];
      allTrainersLoadedRef.current = false;
      setFilteredBatches([]);
      batchPageRef.current = 1;
      
      batchHasMoreRef.current = true;
      batchSearchRef.current = "";
      resetSelectAllBatchState();
      setFieldValue(`events[${index}].batchIds`, []);
      return;
    }

    try {
      trainerLoadingRef.current = true;
      setTrainerLoading(true);
      /* Every trainer, not the first page of them.
         This asked for a single page of 200 and called the result "all":
         past that many trainers, "Select all" quietly selected a subset
         and the event reached nobody else, with nothing on screen to say
         so. fetchAllTrainerOptions walks the pages until they run out, the way
         the batch side already counts before it fetches. */
      const list = await fetchAllTrainerOptions();

      const allValues = [];
      const allLabels = [];
      const allOptions = [];

      list.forEach((t) => {
        const tid = String(t.id || "").trim();
        const label = t.trainerName || `Trainer ${tid.slice(0, 6)}`;
        if (tid) {
          allValues.push(tid);
          allLabels.push(label);
          allOptions.push({
            label,
            value: tid,
            email: t.trainerEmail || "",
          });
        }
      });

      setTrainerOptions(allOptions);
      allTrainersLoadedRef.current = true;
      setFieldValue(`events[${index}].trainerIds`, allValues);
      setFieldValue(`events[${index}].trainerName`, allLabels.join(", "));
      trainerIdsRef.current = allValues;

      setFilteredBatches([]);
      batchPageRef.current = 1;
     
      batchHasMoreRef.current = true;
      batchSearchRef.current = "";
      resetSelectAllBatchState();
      setFieldValue(`events[${index}].batchIds`, []);
    } catch (err) {
      toast.error("Failed to select all trainers");
    } finally {
      trainerLoadingRef.current = false;
      setTrainerLoading(false);
    }
  };

  // ── Select-All Batches ─────────────────────────────────────────────────────
  const handleSelectAllBatches = async (isChecked, setFieldValue, index) => {
    if (!isChecked) {
      setFieldValue(`events[${index}].batchIds`, []);
      resetSelectAllBatchState();
      return;
    }

    const trainerIds = Array.isArray(trainerIdsRef.current)
      ? trainerIdsRef.current.map((x) => String(x).trim()).filter(Boolean)
      : [];
    if (!trainerIds.length) return;

    try {
      setSelectAllBatchLoading(true);
      selectAllBatchLoadingRef.current = true;

      let rawList;

      if (allTrainersLoadedRef.current) {
        /* Every trainer is selected, so "all batches" means the whole
           list -- ask for it directly rather than assembling it from the
           trainers. Asking per-trainer here would also miss any batch
           with no trainer assigned to it, which belongs in an audience
           of "everyone" as much as any other. */
        rawList = await fetchAllBatchOptions();
        if (!rawList.length) {
          toast.info("No batches found.");
          return;
        }
      } else {
        const countResp = await getBatchesByTrainers(
          trainerIds,
          1,
          1,
          "",
          "name",
        );
        const pagination = extractPagination(countResp);
        const totalCount = pagination?.totalCount ?? 0;

        if (totalCount === 0) {
          toast.info("No batches found for the selected trainer(s).");
          return;
        }

        const allResp = await getBatchesByTrainers(
          trainerIds,
          totalCount,
          1,
          "",
          "name",
        );
        rawList = extractBatchList(allResp);
      }

      const allOptions = [];
      const allValues = [];

      rawList.forEach((b) => {
        const bid = String(b?.id || b?._id || "").trim();
        const label = b?.name || bid;
        if (bid) {
          allValues.push(bid);
          batchLabelCacheRef.current.set(bid, label);
          allOptions.push({
            value: bid,
            label,
            trainerId: b.trainerId || null,
          });
        }
      });

      setFilteredBatches((prev) => mergePaginatedOptions(prev, allOptions));
      setFieldValue(`events[${index}].batchIds`, allValues);
      setSelectAllBatchDone(true);
      setSelectAllBatchTotal(allValues.length);
    } catch (err) {
      console.error("Failed to select all batches:", err);
      toast.error("Failed to select all batches");
    } finally {
      setSelectAllBatchLoading(false);
      selectAllBatchLoadingRef.current = false;
    }
  };

  // ── Render guard ───────────────────────────────────────────────────────────
  if (!eventData || loading) {
    return (
      <div className="att-picklist-foot">
        <span className="pg-spinner att-spinner" aria-hidden="true" />
        <span>Loading event...</span>
      </div>
    );
  }

  const isReadOnly = userRole === "student";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    /* --picker, not att-embed alone: the plain .att-embed rule makes
       every .nas-options a block with overflow visible, so the option
       list never scrolls, onScroll never fires and onMenuScrollToBottom
       with it -- the pickers here stopped at their first 20. The
       modifier gives those lists their scrolling box back. */
    <div className="att-embed att-embed--picker">
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={!isReadOnly ? validationSchema : undefined}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleSubmit: formikSubmit,
          isSubmitting,
          errors,
          touched,
          setFieldValue,
        }) => {
          const ev = values.events[0];
          const isHoliday = ev.category === "Holiday";
          const steps = eventSteps(isHoliday);
          const last = steps.length;

          // A holiday drops the whole third step, so a reader who had
          // reached it and then switched category is brought back in range.
          const now = Math.min(step, last);
          const showing = Math.min(shownStep, last);

          const evErrors = errors.events?.[0] || {};
          const evTouched = touched.events?.[0] || {};
          const at = (f) => `events[0].${f}`;
          const err = (f) => (evTouched[f] ? evErrors[f] : "");
          const done = (n) => eventStepComplete(n, ev, evErrors, isHoliday);
          const allDone = steps.every((_, i) => done(i + 1));

          const trainerValue = trainerOptions.filter((o) =>
            ev.trainerIds?.some((id) => String(id) === String(o.value)),
          );

          return (
            <form className="att-form" noValidate onSubmit={formikSubmit}>
              <ol className="att-steps">
                {steps.map((s, i) => {
                  const n = i + 1;
                  const state = n === now ? "is-now" : n < now ? "is-done" : "";
                  return (
                    <li className={`att-step-tab ${state}`.trim()} key={s.label}>
                      <button
                        type="button"
                        disabled={n === now || (!isEditing && n > now)}
                        onClick={() => setStep(n)}
                      >
                        <span className="att-step-n">{n}</span>
                        {s.label}
                      </button>
                    </li>
                  );
                })}
              </ol>

              <div className={`att-stage is-${phase}`}>
                <div className="att-stage-inner" key={showing}>
                  {/* ── 1. What the event is ─────────────────────────── */}
                  {showing === 1 && (
                    <section className="att-step">
                      <header className="att-step-head">
                        <h5>{steps[0].title}</h5>
                        <p>{steps[0].hint}</p>
                      </header>

                      <div className="mcq-options">
                        <div className="pg-field">
                          <label className="pg-label">Category</label>
                          <NewCustomAsyncSelectDropdown
                            options={categoryOptions}
                            value={categoryOptions.filter(
                              (o) => o.value === ev.category,
                            )}
                            onChange={(opts) =>
                              handleCategoryChange(
                                Array.isArray(opts) && opts.length
                                  ? opts[opts.length - 1]
                                  : null,
                                setFieldValue,
                              )
                            }
                            placeholder="Select a category"
                            isMulti={false}
                            isSearchable={true}
                            searchInControl={true}
                            isDisabled={isReadOnly}
                          />
                          {err("category") && (
                            <p className="att-error">{err("category")}</p>
                          )}
                        </div>

                        {!isHoliday && (
                          <div className="pg-field">
                            <label className="pg-label" htmlFor="ev-name">
                              Event name
                            </label>
                            <input
                              id="ev-name"
                              type="text"
                              className="pg-input"
                              placeholder="Enter event name"
                              name={at("name")}
                              value={ev.name || ""}
                              onChange={handleChange}
                              disabled={isReadOnly}
                            />
                            {err("name") && (
                              <p className="att-error">{err("name")}</p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label">Priority</label>
                        <NewCustomAsyncSelectDropdown
                          options={priorityOptions}
                          value={priorityOptions.filter(
                            (o) => o.value === ev.priority,
                          )}
                          onChange={(opts) =>
                            setFieldValue(
                              at("priority"),
                              Array.isArray(opts) && opts.length
                                ? opts[opts.length - 1].value
                                : PRIORITY_NOT_SET,
                            )
                          }
                          placeholder="Select a priority"
                          isMulti={false}
                          isSearchable={true}
                          searchInControl={true}
                          isDisabled={isReadOnly}
                        />
                        {err("priority") && (
                          <p className="att-error">{err("priority")}</p>
                        )}
                      </div>

                      {isHoliday ? (
                        <div className="mcq-options">
                          <div className="pg-field">
                            <label className="pg-label" htmlFor="ev-start">
                              Starts
                            </label>
                            <input
                              id="ev-start"
                              type="datetime-local"
                              className="pg-input"
                              value={toLocalInput(ev.startDateTime)}
                              onChange={(e) =>
                                setFieldValue(
                                  at("startDateTime"),
                                  fromLocalInput(e.target.value),
                                )
                              }
                              disabled={isReadOnly}
                            />
                            {err("startDateTime") && (
                              <p className="att-error">{err("startDateTime")}</p>
                            )}
                          </div>

                          <div className="pg-field">
                            <label className="pg-label" htmlFor="ev-end">
                              Ends
                            </label>
                            <input
                              id="ev-end"
                              type="datetime-local"
                              className="pg-input"
                              value={toLocalInput(ev.endDateTime)}
                              onChange={(e) =>
                                setFieldValue(
                                  at("endDateTime"),
                                  fromLocalInput(e.target.value),
                                )
                              }
                              disabled={isReadOnly}
                            />
                            {err("endDateTime") && (
                              <p className="att-error">{err("endDateTime")}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="mcq-options">
                          <div className="pg-field">
                            <label className="pg-label" htmlFor="ev-date">
                              Date
                            </label>
                            <input
                              id="ev-date"
                              type="date"
                              className="pg-input"
                              value={ev.date || ""}
                              min={isEditing ? undefined : todayInputValue()}
                              onChange={(e) =>
                                setFieldValue(at("date"), e.target.value)
                              }
                              disabled={isReadOnly}
                            />
                            {err("date") && (
                              <p className="att-error">{err("date")}</p>
                            )}
                          </div>

                          <div className="pg-field">
                            <label className="pg-label" htmlFor="ev-time">
                              Start time
                            </label>
                            <input
                              id="ev-time"
                              type="time"
                              className="pg-input"
                              value={ev.time || ""}
                              onChange={(e) =>
                                setFieldValue(at("time"), e.target.value)
                              }
                              disabled={isReadOnly}
                            />
                            {err("time") && (
                              <p className="att-error">{err("time")}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </section>
                  )}

                  {/* ── 2. Who it reaches ────────────────────────────── */}
                  {showing === 2 && (
                    <section className="att-step">
                      <header className="att-step-head">
                        <h5>{steps[1].title}</h5>
                        <p>{steps[1].hint}</p>
                      </header>

                      <div className="pg-field">
                        <label className="pg-label">
                          Trainers
                          <span className="att-card-count">
                            {ev.trainerIds?.length || 0} chosen
                          </span>
                        </label>

                        {isTrainer && !isEditing ? (
                          <input
                            type="text"
                            className="pg-input"
                            readOnly
                            disabled
                            value={
                              ev.trainerName ||
                              `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
                              "You"
                            }
                          />
                        ) : (
                          <>
                            {!isReadOnly && (
                              <div className="cal-picker-tools">
                                <button
                                  type="button"
                                  className="pg-btn pg-btn--tool"
                                  disabled={trainerLoading}
                                  onClick={() =>
                                    handleSelectAllTrainers(true, setFieldValue, 0)
                                  }
                                >
                                  Select all
                                </button>
                                <button
                                  type="button"
                                  className="pg-btn pg-btn--tool"
                                  disabled={!ev.trainerIds?.length}
                                  onClick={() =>
                                    handleSelectAllTrainers(false, setFieldValue, 0)
                                  }
                                >
                                  Clear
                                </button>
                                <button
                                  type="button"
                                  className="pg-btn pg-btn--tool"
                                  onClick={refreshTrainers}
                                >
                                  Refresh
                                </button>
                              </div>
                            )}
                            <NewCustomAsyncSelectDropdown
                              isMulti
                              options={trainerOptions}
                              value={trainerValue}
                              onChange={(opts) =>
                                onTrainersChange(opts, setFieldValue, 0, ev.batchIds)
                              }
                              onMenuOpen={handleTrainerMenuOpen}
                              onInputChange={handleTrainerInputChange}
                              onMenuScrollToBottom={handleTrainerScrollToBottom}
                              onRefresh={refreshTrainers}
                              isLoading={trainerLoading || trainerSearchLoading}
                              placeholder="Select trainer(s)"
                              isSearchable={true}
                              searchInControl={true}
                              isDisabled={isReadOnly}
                            />
                          </>
                        )}
                        {err("trainerIds") && (
                          <p className="att-error">{err("trainerIds")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label">
                          Batches
                          <span className="att-card-count">
                            {ev.batchIds?.length || 0} chosen
                          </span>
                        </label>

                        {!isReadOnly && (
                          <div className="cal-picker-tools">
                            <button
                              type="button"
                              className="pg-btn pg-btn--tool"
                              disabled={
                                batchLoading ||
                                selectAllBatchLoading ||
                                !ev.trainerIds?.length
                              }
                              onClick={() =>
                                handleSelectAllBatches(true, setFieldValue, 0)
                              }
                            >
                              Select all
                            </button>
                            <button
                              type="button"
                              className="pg-btn pg-btn--tool"
                              disabled={!ev.batchIds?.length}
                              onClick={() =>
                                handleSelectAllBatches(false, setFieldValue, 0)
                              }
                            >
                              Clear
                            </button>
                            <button
                              type="button"
                              className="pg-btn pg-btn--tool"
                              onClick={refreshBatches}
                            >
                              Refresh
                            </button>
                          </div>
                        )}

                        <NewCustomAsyncSelectDropdown
                          key={`batch-${ev.trainerIds ? ev.trainerIds.join(",") : "empty"}`}
                          isMulti
                          options={filteredBatches}
                          value={resolveBatchValue(ev.batchIds)}
                          onChange={(opts) => onBatchesChange(opts, setFieldValue, 0)}
                          onMenuOpen={handleBatchMenuOpen}
                          onMenuScrollToBottom={handleBatchScrollToBottom}
                          onRefresh={refreshBatches}
                          isLoading={batchLoading || selectAllBatchLoading}
                          isSearchable={true}
                          searchInControl={true}
                          isDisabled={
                            isReadOnly || !ev.trainerIds || ev.trainerIds.length === 0
                          }
                          placeholder={
                            !ev.trainerIds || ev.trainerIds.length === 0
                              ? "Pick a trainer first"
                              : selectAllBatchLoading
                                ? "Loading all batches..."
                                : batchLoading
                                  ? "Loading batches..."
                                  : filteredBatches.length === 0
                                    ? "No batches for those trainers"
                                    : "Select batch(es)"
                          }
                        />

                        {selectAllBatchDone && selectAllBatchTotal > 0 && (
                          <p className="att-note att-note--ok">
                            All {selectAllBatchTotal} batches selected.
                          </p>
                        )}
                        {err("batchIds") && (
                          <p className="att-error">{err("batchIds")}</p>
                        )}
                      </div>
                    </section>
                  )}

                  {/* ── 3. Where it happens, and who runs it ─────────── */}
                  {showing === 3 && !isHoliday && (
                    <section className="att-step">
                      <header className="att-step-head">
                        <h5>{steps[2].title}</h5>
                        <p>{steps[2].hint}</p>
                      </header>

                      <div className="mcq-options">
                        <div className="pg-field">
                          <label className="pg-label">Venue</label>
                          <NewCustomAsyncSelectDropdown
                            options={classroomOptions}
                            value={classroomOptions.filter(
                              (o) => o.value === ev.venueId,
                            )}
                            onChange={(opts) => {
                              const opt =
                                Array.isArray(opts) && opts.length
                                  ? opts[opts.length - 1]
                                  : null;
                              setFieldValue(at("venueId"), opt?.value || "");
                              setFieldValue(
                                at("classroomNumber"),
                                opt?.classroomNo || "",
                              );
                            }}
                            onMenuOpen={handleClassroomMenuOpen}
                            onMenuScrollToBottom={handleClassroomScrollToBottom}
                            onRefresh={refreshClassrooms}
                            isLoading={classroomLoading || classroomSearchLoading}
                            placeholder="Select a venue"
                            isMulti={false}
                            isSearchable={true}
                            searchInControl={true}
                            isDisabled={isReadOnly}
                          />
                          {err("venueId") && (
                            <p className="att-error">{err("venueId")}</p>
                          )}
                        </div>

                        <div className="pg-field">
                          <label className="pg-label" htmlFor="ev-link">
                            Online link
                          </label>
                          <input
                            id="ev-link"
                            type="url"
                            className="pg-input"
                            placeholder="https://..."
                            name={at("onlineLink")}
                            value={ev.onlineLink || ""}
                            onChange={handleChange}
                            disabled={isReadOnly}
                          />
                          {err("onlineLink") && (
                            <p className="att-error">{err("onlineLink")}</p>
                          )}
                        </div>
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ev-address">
                          Offline address
                        </label>
                        <input
                          id="ev-address"
                          type="text"
                          className="pg-input"
                          placeholder="Where to be, in words"
                          name={at("offlineAddress")}
                          value={ev.offlineAddress || ""}
                          onChange={handleChange}
                          disabled={isReadOnly}
                        />
                      </div>

                      <div className="mcq-options">
                        <div className="pg-field">
                          <label className="pg-label" htmlFor="ev-org-name">
                            Organiser
                          </label>
                          <input
                            id="ev-org-name"
                            type="text"
                            className="pg-input"
                            placeholder="Name"
                            name={at("organizerName")}
                            value={ev.organizerName || ""}
                            onChange={handleChange}
                            disabled={isReadOnly}
                          />
                          {err("organizerName") && (
                            <p className="att-error">{err("organizerName")}</p>
                          )}
                        </div>

                        <div className="pg-field">
                          <label className="pg-label" htmlFor="ev-org-email">
                            Organiser email
                          </label>
                          <input
                            id="ev-org-email"
                            type="email"
                            className="pg-input"
                            placeholder="name@example.com"
                            name={at("organizerEmail")}
                            value={ev.organizerEmail || ""}
                            onChange={handleChange}
                            disabled={isReadOnly}
                          />
                          {err("organizerEmail") && (
                            <p className="att-error">{err("organizerEmail")}</p>
                          )}
                        </div>

                        <div className="pg-field">
                          <label className="pg-label" htmlFor="ev-org-phone">
                            Organiser phone
                          </label>
                          <input
                            id="ev-org-phone"
                            type="tel"
                            className="pg-input"
                            placeholder="Contact number"
                            name={at("organizerPhone")}
                            value={ev.organizerPhone || ""}
                            onChange={handleChange}
                            disabled={isReadOnly}
                          />
                          {err("organizerPhone") && (
                            <p className="att-error">{err("organizerPhone")}</p>
                          )}
                        </div>
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="ev-desc">
                          Description
                        </label>
                        <textarea
                          id="ev-desc"
                          rows={4}
                          className="pg-input pg-textarea"
                          placeholder="What the session covers"
                          value={ev.description || ""}
                          onChange={(e) =>
                            setFieldValue(at("description"), e.target.value)
                          }
                          disabled={isReadOnly}
                        />
                        {err("description") && (
                          <p className="att-error">{err("description")}</p>
                        )}
                      </div>

                      <div className="mcq-options">
                        <div className="pg-field">
                          <label className="pg-label" htmlFor="ev-agenda">
                            Agenda <span className="att-card-count">optional</span>
                          </label>
                          <textarea
                            id="ev-agenda"
                            rows={3}
                            className="pg-input pg-textarea"
                            name={at("agenda")}
                            value={ev.agenda || ""}
                            onChange={handleChange}
                            disabled={isReadOnly}
                          />
                        </div>

                        <div className="pg-field">
                          <label className="pg-label" htmlFor="ev-highlights">
                            Highlights{" "}
                            <span className="att-card-count">optional</span>
                          </label>
                          <textarea
                            id="ev-highlights"
                            rows={3}
                            className="pg-input pg-textarea"
                            name={at("highlights")}
                            value={ev.highlights || ""}
                            onChange={handleChange}
                            disabled={isReadOnly}
                          />
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </div>

              <div className="att-actions">
                {now > 1 ? (
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={() => setStep(now - 1)}
                  >
                    Back
                  </button>
                ) : (
                  <button type="button" className="pg-btn" onClick={onCancel}>
                    Cancel
                  </button>
                )}

                {/* NEITHER OF THESE MAY BE type="submit", AND THEY MAY NOT
                    SHARE A DOM NODE.

                    They sit at the same position under the same parent, so
                    React reconciled them as ONE <button> and simply patched
                    its `type`. Clicking Continue then did this:

                      1. the click lands on a type="button"
                      2. onClick runs setStep(now + 1); React 18 flushes a
                         discrete event synchronously, so the DOM is already
                         patched before the click finishes
                      3. if that step was the last one, the branch flips and
                         the SAME element is now type="submit"
                      4. the browser then runs the click's activation
                         behaviour, reads the type it finds NOW, and submits

                    So Continue saved the event instead of advancing -- on a
                    Holiday, which has only two steps, that happened on the
                    very first click: "Continue" saved rather than opening
                    Audience.

                    The submit is explicit now, and the keys stop the two
                    from ever being the same element. Nothing in this form
                    is type="submit" any more, which also means Enter in a
                    text field can no longer save a half-filled event. */}
                {now < last ? (
                  <button
                    key="step-next"
                    type="button"
                    className="pg-btn pg-btn--main"
                    disabled={!done(now)}
                    onClick={() => setStep(now + 1)}
                  >
                    Continue
                  </button>
                ) : (
                  !isReadOnly && (
                    <button
                      key="step-save"
                      type="button"
                      className="pg-btn pg-btn--main"
                      disabled={isSubmitting || !allDone}
                      onClick={() => formikSubmit()}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="pg-spinner att-spinner"
                            aria-hidden="true"
                          />
                          {isEditing ? "Updating..." : "Creating..."}
                        </>
                      ) : isEditing ? (
                        "Update event"
                      ) : (
                        "Create event"
                      )}
                    </button>
                  )
                )}
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}

export default AddEventModal;
